import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["itemsContainer", "template", "itemRow", "grandTotal", "supplierSelect"]
  static values  = { productsUrl: String }

  connect() {
    this.updateGrandTotal()
    // If a supplier is already selected (edit form), load its products
    if (this.hasSupplierSelectTarget && this.supplierSelectTarget.value) {
      this._loadProducts(this.supplierSelectTarget.value)
    }
  }

  supplierChanged(event) {
    const supplierId = event.target.value
    if (!supplierId) {
      this._setProductOptions([])
      return
    }
    this._loadProducts(supplierId)
  }

  async _loadProducts(supplierId) {
    const url = this.productsUrlValue.replace("{id}", supplierId)
    try {
      const res  = await fetch(url, { headers: { Accept: "application/json" } })
      const data = await res.json()
      this._setProductOptions(data)
    } catch {
      // silently fail — leave selects as-is
    }
  }

  _setProductOptions(products) {
    this.itemRowTargets.forEach(row => {
      if (row.style.display === "none") return
      const select   = row.querySelector("[data-product-select]")
      if (!select) return
      const current  = select.value
      select.innerHTML = '<option value="">Select product</option>' +
        products.map(p =>
          `<option value="${p.id}"${p.id == current ? " selected" : ""}>${p.name}</option>`
        ).join("")
    })
  }

  addItem(event) {
    event.preventDefault()
    const timestamp = new Date().getTime()
    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, timestamp)
    this.itemsContainerTarget.insertAdjacentHTML("beforeend", html)

    // Populate the new row's product select immediately
    const rows     = this.itemRowTargets
    const newRow   = rows[rows.length - 1]
    const supplierId = this.hasSupplierSelectTarget && this.supplierSelectTarget.value
    if (supplierId) {
      this._loadProductsForRow(supplierId, newRow)
    }
    newRow?.querySelector("select")?.focus()
  }

  async _loadProductsForRow(supplierId, row) {
    const url = this.productsUrlValue.replace("{id}", supplierId)
    try {
      const res  = await fetch(url, { headers: { Accept: "application/json" } })
      const data = await res.json()
      const select = row.querySelector("[data-product-select]")
      if (!select) return
      select.innerHTML = '<option value="">Select product</option>' +
        data.map(p => `<option value="${p.id}">${p.name}</option>`).join("")
    } catch { /* ignore */ }
  }

  removeItem(event) {
    event.preventDefault()
    const row = event.target.closest("[data-po-form-target='itemRow']")
    if (!row) return

    const destroyField = row.querySelector("[data-destroy-field]")
    if (destroyField) {
      destroyField.value = "1"
      row.style.display = "none"
    } else {
      row.remove()
    }
    this.updateGrandTotal()
  }

  updateLineTotal(event) {
    const row = event.target.closest("[data-po-form-target='itemRow']")
    if (!row) return
    this._recalcRow(row)
    this.updateGrandTotal()
  }

  updateGrandTotal() {
    let total = 0
    this.itemRowTargets.forEach(row => {
      if (row.style.display === "none") return
      const qty  = parseFloat(row.querySelector("[data-qty-field]")?.value) || 0
      const cost = parseFloat(row.querySelector("[data-cost-field]")?.value) || 0
      total += qty * cost
    })
    if (this.hasGrandTotalTarget) {
      this.grandTotalTarget.textContent = total > 0
        ? `PKR ${total.toLocaleString("en-PK", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
        : ""
    }
  }

  _recalcRow(row) {
    const qty   = parseFloat(row.querySelector("[data-qty-field]")?.value) || 0
    const cost  = parseFloat(row.querySelector("[data-cost-field]")?.value) || 0
    const total = qty * cost
    const label = row.querySelector("[data-line-total]")
    if (label) {
      label.textContent = total > 0
        ? `PKR ${total.toLocaleString("en-PK", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
        : "—"
    }
  }
}
